import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select'
import { createPopper } from '@popperjs/core'

export default class extends Controller {
  static values = {
    selectedIds: Array,
    dragAndDrop: { type: Boolean, default: false },
    dropdownInput: { type: Boolean, default: false },
    dropdownParent: { type: String, default: '' },
    allowEmptyOption: { type: Boolean, default: false },
    allowCreate: { type: Boolean, default: false },
    usePopper: { type: Boolean, default: false },
    useCustomOptionRender: { type: Boolean, default: false },
    useCustomItemRender: { type: Boolean, default: false },
    useRedirect: { type: Boolean, default: false },
    closeAfterSelect: { type: Boolean, default: true },
    dispatchEvent: { type: Boolean, default: false },
    refreshThrottle: { type: Number, default: 0 },
    lockOptgroupOrder: { type: Boolean, default: false },
    // An accessible name for the visible combobox, for the cases TomSelect cannot name itself.
    //
    // TomSelect names its widget by looking for `label[for="<select id>"]` and nothing else — it
    // never reads `aria-label` off the select. So a select with a visible label that has no `for`,
    // or one of several selects sharing an id, ends up with a widget announcing nothing at all.
    // Pass the label text here in those cases. CU-86cb4v1wb.
    label: { type: String, default: '' }
  }

  connect() {
    // skip TomSelect in pdf views, just regular select will be rendered
    const metaEl = document.querySelector("meta[name='pdf']")
    if(metaEl && metaEl.getAttribute('content') == 'true') {
      return
    }

    const el = this.element
    const htmlEl = document.getElementsByTagName('html')[0]

    let config = {
      maxOptions: null,
      hidePlaceholder: true, // hides placeholder when some options are already selected
      duplicates: true,
      closeAfterSelect: this.closeAfterSelectValue,
      // Do not open the dropdown just because the control received focus. With the `dropdown_input`
      // plugin, opening on focus moves focus into a hidden input inside the dropdown and the close
      // hook then forces it back to the control — so tabbing through a column of these (e.g. the
      // admin new-user Organisational Structure section) pops every dropdown open and bounces focus
      // around, and a screen reader can be thrown to the top of the page (CU-1248uf3utnm, 2.4.3).
      // A click still opens it; focus alone no longer does — which also matches a native <select>.
      openOnFocus: false,
      lockOptgroupOrder: this.lockOptgroupOrderValue // maintain the order of optgroups as defined in the optgroups array
    }

    if(this.allowEmptyOptionValue) {
      config.allowEmptyOption = true
    }

    const context = this

    if(this.usePopperValue) {
      const popper_options = {
        modifiers: [
          {
            name: 'flip',
            options: {
              flipVariations: false
            }
          }
        ]
      }

      config.onInitialize = function() {
        // this in context of this function is instance of TomSelect
        this.popper = createPopper(this.control, this.dropdown, popper_options)
      }
    }

    config.onDropdownOpen = function() {
      // `this` here is the TomSelect instance, so read the controller's usePopperValue
      // via the captured `context`. Recompute on open: the popper created in onInitialize
      // ran while the dropdown was display:none (zero-size) and the modal was still settling,
      // so its placement is stale; update() repositions against the now-visible control.
      if(context.usePopperValue && this.popper) {
        this.popper.update()
      }

      // A body-parented dropdown is positioned once, before this callback, while it still sits at its
      // default spot below the viewport. On pages that scroll inside a panel rather than <html>, that
      // briefly adds a page scrollbar, so the first open measures a narrower, shifted control. The
      // dropdown is under the control by now and the scrollbar is gone, so measure again.
      if(this.settings.dropdownParent === 'body') {
        this.positionDropdown()
      }

      htmlEl.setAttribute('data-disable-scroll', 'true')

      // add data attribute to parent table responsive of current TomSelect instance
      let tableResponsiveEl = context.element.closest('.table-responsive')
      if(tableResponsiveEl) {
        tableResponsiveEl.setAttribute('data-has-tom-select-opened', true)
      }
    }

    config.onDropdownClose = function() {
      htmlEl.removeAttribute('data-disable-scroll')

      let tableResponsiveEl = context.element.closest('.table-responsive')
      if(tableResponsiveEl) {
        tableResponsiveEl.removeAttribute('data-has-tom-select-opened')
      }
    }

    // Position Dropdown
    if(this.dropdownParentValue && this.dropdownParentValue != '') {
      // use dropdownParent selector passed with data
      config.dropdownParent = this.dropdownParentValue
    } else if(this.inTableResponsive() && !this.inModal()) {
      // if dropdown rendered in .table-responsive but not in modal use body tag
      config.dropdownParent = 'body'
    }

    if(el.multiple) {
      config.plugins = [
        'dropdown_input',
        'remove_button'
      ]

      if(this.inFilter()) {
        config.plugins.push('input_autogrow')
      }

      // workaround to keep custom order of items, independent of order of available options
      if(this.selectedIdsValue && this.selectedIdsValue.length > 0) {
        config.items = this.selectedIdsValue
      }

      if(!el.getAttribute('placeholder')) {
        config.placeholder = 'Select some options'
      }
    } else { // single select
      config.plugins = ['clear_button']

      if(this.dropdownInputValue) {
        config.plugins.push('dropdown_input')
      }

      if(!el.getAttribute('placeholder')) {
        config.placeholder ='Select an option'
      }

      // redirect page to the url specified as data-tomselect-redirect-url option attribute
      if(this.useRedirectValue) {
        config.onChange = function(value) {
          const originalSelect = this.input
          const selectedOption = originalSelect.querySelector(`option[value="${value}"]`)

          const redirectURL = selectedOption.getAttribute('data-tomselect-redirect-url')
          if(redirectURL) {
            window.location.href = redirectURL
          }
        }
      }
    }

    if(this.allowCreateValue) {
      config.create = true
      config.createOnBlur = true

      config.createFilter = function(input) {
        input = input.toLowerCase()
        return !(input in this.options)
      }
    }

    config.render = {}

    // custom render for options
    if(this.useCustomOptionRenderValue) {
      config.render.option = (data, escape) => {
        return `<div value="${data.value}"` +
            (data.extraCssClass ? ` class="${data.extraCssClass}">` : '>') +
            escape(data.text) +
            '</div>'
      }
    }

    // custom render for selected option
    if(this.useCustomItemRenderValue) {
      config.render.item = function(data, escape) {
        return `<div value="${data.value}"` +
            (data.extraCssClass ? ` class="${data.extraCssClass}">` : '>') +
            escape(data.text) +
            '</div>'
      }
    }

    if(this.dragAndDropValue) {
      config.onInitialize = async () => {
        const Sortable = await this.#sortableConditionalImport()

        const tsControl = this.tomSelect.control
        if(!tsControl) {
          return
        }

        const sortable = Sortable.create(tsControl, {
          handle: '.ts-item-drag-handle',
          animation: 150,
          draggable: '.ts-item-draggable',
          forceFallback: true, // Force HTML5 DnD fallback for better compatibility
          fallbackClass: 'sortable-fallback',
          ghostClass: 'sortable-ghost',
          chosenClass: 'sortable-chosen',
          dragClass: 'sortable-drag',
          onChoose: (event) => {
            // Prevent TomSelect from interfering
            event.item.style.pointerEvents = 'none'
          },
          onEnd: (event) => {
            // Restore pointer events
            event.item.style.pointerEvents = 'auto'
          },
          onUpdate: (event) => {
            // Get the new order from the DOM items
            const items = Array.from(tsControl.querySelectorAll('.item'))
            const newOrder = []

            // Get current values and items from TomSelect
            const currentValues = this.tomSelect.getValue()

            // Map DOM order to values by matching text content
            items.forEach((domItem, index) => {
              const itemText = domItem.textContent.replace('⋮⋮', '').replace('×', '').trim()
              // Find the corresponding value by matching text
              for (let value of currentValues) {
                const option = this.tomSelect.options[value]
                if (option && option.text === itemText) {
                  newOrder.push(value)
                  break
                }
              }
            })

            // Update TomSelect with the new order
            if (newOrder.length > 0 && newOrder.length === currentValues.length) {
              this.tomSelect.setValue(newOrder, true) // true = silent (no events)
            }
          }
        })
      }

      config.render.item = function(data, escape) {
        return `<div class="item ts-item-draggable">
                <span class="ts-item-drag-handle si-arrow-double-h-circle-o"></span>
                ${escape(data.text)}
            </div>`
      }
    }

    if(this.refreshThrottleValue !== undefined && this.refreshThrottleValue !== null && this.refreshThrottleValue !== 0) {
      config.refreshThrottle = this.refreshThrottleValue
    }

    if(this.dispatchEventValue) {
      config.onInitialize = () => {
        const ts_initialized_event = new CustomEvent('tomselect:initialized', { detail: { selector: `#${el.getAttribute('id')}` } })
        document.dispatchEvent(ts_initialized_event)
      }
    }

    if(this.closeAfterSelectValue == false) {
      config.onItemAdd = () => {
        this.tomSelect.setTextboxValue('')
        this.tomSelect.refreshOptions(false)
      }
    }

    this.tomSelect = new TomSelect(el, config)
    this.repairDropdownInputCombobox()
    this.repairAccessibility(el)
    this.openDropdownOnControlClick()
  }

  // Companion to `openOnFocus: false`. With that setting the dropdown no longer opens when the
  // control merely receives focus — which is what stops tabbing through a column of these (the admin
  // Organisational Structure section, CU-1248uf3utnm) from popping every dropdown open and throwing a
  // screen reader to the top. But `openOnFocus: false` also stops a pointer click opening it, so the
  // control would look dead to a mouse user. Restore that here: an explicit click opens it, focus
  // alone still does not. Keyboard users open it with ArrowDown/Enter as usual.
  //
  // `wasOpen` is read on mousedown, before TomSelect processes the click, so that clicking an already
  // open control still closes it (toggle) rather than being immediately reopened.
  openDropdownOnControlClick() {
    let wasOpen = false
    this.tomSelect.control.addEventListener('mousedown', () => { wasOpen = this.tomSelect.isOpen })
    this.tomSelect.control.addEventListener('click', () => {
      if(!wasOpen && !this.tomSelect.isOpen) {
        this.tomSelect.open()
      }
    })
  }

  // TomSelect's `dropdown_input` plugin leaves a multi-select impossible to complete without a
  // mouse. CU-1248uf3tdjm, WCAG 2.1.1 Keyboard, level A — heard in the September 2026 listening
  // pass as "Blank, Select some options" and nothing else, however far you arrowed.
  //
  // The plugin moves the real <input> into the dropdown and focuses it while the dropdown is open
  // ("give the control_input focus when the dropdown is open", its own comment), but leaves
  // `focus_node` pointing at the control div — so `role="combobox"` and, at runtime,
  // `aria-expanded` and `aria-activedescendant` are all written to an element that does not hold
  // focus. `aria-activedescendant` only means anything on the focused element, so arrowing moved
  // the highlight and told assistive technology nothing. What was left to hear is the
  // `items-placeholder` input the plugin injects, which is where the reported "Select some
  // options" came from.
  //
  // THE FIRST ATTEMPT AT THIS BROKE EVERY MULTI-SELECT. It reassigned `focus_node` to the input so
  // TomSelect's own runtime writes would land on the focused element. But the plugin also does
  // this, on close:
  //
  //     // return focus to control to allow further keyboard input
  //     self.hook('before', 'close', () => self.focus_node.focus({ preventScroll: true }))
  //
  // With focus_node pointing into the dropdown, that focused an element being hidden, so focus fell
  // to <body>. `closeAfterSelect` is true for multi-selects, so the control died after one pick.
  // Reverted by Artur in d9881b7b before it could reach production.
  //
  // So this version touches nothing TomSelect owns. The control keeps `role="combobox"` and stays
  // `focus_node`, so the plugin's focus contract is intact and closing still returns you to a
  // usable control. The input — which is what holds focus while the list is open — is given
  // `aria-controls`, the same accessible name, and a mirror of the two attributes TomSelect writes
  // at runtime. A MutationObserver rather than a hook, so this depends on no internal method name.
  //
  // Nothing moves on screen, and nothing changes for a mouse user.
  repairDropdownInputCombobox() {
    const ts = this.tomSelect
    if (!ts) return

    const control = ts.control
    const input = ts.control_input
    if (!control || !input || control === input) return

    // Test the situation, not the plugin name: has the input been moved inside the dropdown? That
    // is the split between the focused element and the element carrying the ARIA. Widgets whose
    // input still sits in the control are already correct and are left alone.
    if (!ts.dropdown || !ts.dropdown.contains(input)) return

    // The focused input had no role, so VoiceOver announced it only as "edit text" and — because
    // Safari+VoiceOver has long-standing weak support for `aria-activedescendant` on a bare textbox —
    // read nothing as you arrowed the options (Chrome+VoiceOver followed activedescendant regardless).
    // Safari / iOS (WebKit) + VoiceOver is the one engine that needs help here: it has long-standing
    // weak support for `aria-activedescendant`, so it announces nothing as you arrow the options,
    // while Chrome+VoiceOver and NVDA/JAWS follow the control's activedescendant and already work.
    const isWebKit = navigator.vendor === 'Apple Computer, Inc.'

    // On WebKit only, make the focused input an explicit combobox so VoiceOver recognises the control
    // it is sitting on (otherwise just "edit text"). Gated: the control is already the widget's one
    // combobox, so on engines that work via activedescendant a second one would only duplicate it —
    // and the estate asserts one combobox per widget (tomselect_keyboard_spec). role only; focus,
    // keyboard and type-ahead are untouched. CU-1248uf3v2b8.
    if (isWebKit) input.setAttribute('role', 'combobox')

    // Copied, not moved. The control keeps its own role and name for when focus returns to it.
    const listboxId = control.getAttribute('aria-controls')
    if (listboxId) input.setAttribute('aria-controls', listboxId)

    const labelledBy = control.getAttribute('aria-labelledby')
    const label = control.getAttribute('aria-label')
    if (labelledBy) input.setAttribute('aria-labelledby', labelledBy)
    else if (label) input.setAttribute('aria-label', label)

    // Safari / iOS (WebKit) + VoiceOver still do not announce the active option as
    // `aria-activedescendant` moves on Up/Down — a long-standing WebKit gap — even with the combobox
    // pattern now complete. (Chrome+VoiceOver and NVDA/JAWS do announce it; VoiceOver's own item
    // navigation, Ctrl+Opt+arrows, also reads the options. It is specifically the Up/Down path on
    // WebKit that stays silent.) Mirror the active option's text into a live region so each Up/Down is
    // spoken. Gated to Apple's engine so Chrome+VoiceOver does not double-announce. The region lives
    // inside the TomSelect wrapper, so destroy() removes it with everything else. CU-1248uf3v2b8.
    let liveRegion = null
    if (isWebKit) {
      liveRegion = document.createElement('div')
      liveRegion.className = 'visually-hidden'
      liveRegion.setAttribute('aria-live', 'assertive')
      ts.wrapper.appendChild(liveRegion)
    }

    let lastAnnounced = ''
    const announceActive = () => {
      if (!liveRegion) return
      const id = control.getAttribute('aria-activedescendant')
      const option = id ? ts.dropdown.querySelector(`#${CSS.escape(id)}`) : null
      const text = option ? option.textContent.trim() : ''
      // Only write on an actual change. mirror() also runs on aria-expanded, so an open/close toggle
      // would otherwise rewrite the same text and make the assertive region re-announce the current
      // option — stepping on, say, a selection confirmation.
      if (text === lastAnnounced) return
      lastAnnounced = text
      liveRegion.textContent = text
    }

    const mirror = () => {
      ['aria-activedescendant', 'aria-expanded'].forEach((attr) => {
        const value = control.getAttribute(attr)
        if (value === null) input.removeAttribute(attr)
        else input.setAttribute(attr, value)
      })
      announceActive()
    }
    mirror()
    new MutationObserver(mirror).observe(control, {
      attributes: true,
      attributeFilter: ['aria-activedescendant', 'aria-expanded']
    })
  }

  // TomSelect leaves two accessibility problems behind, on every dropdown, in every product.
  // Neither is our code's doing — both are the library's defaults — so this repairs them after
  // initialisation rather than trying to change what TomSelect does. CU-86cb4v1wb.
  repairAccessibility(el) {
    // 1. The native select stays exposed to assistive technology with no name.
    //
    // TomSelect hides it with `.ts-hidden-accessible`, which clips it rather than removing it, and
    // moves our `<label for>` to its own widget. So the original is left in the accessibility tree,
    // nameless, duplicating every option — 31 of them on the admin new-user form alone. It already
    // carries `tabindex="-1"` from TomSelect, so hiding it cannot orphan focus.
    el.setAttribute('aria-hidden', 'true')

    // 2. The visible widget may have no name either.
    //
    // TomSelect looks for `label[for="<select id>"]` and nothing else. A visible label without a
    // `for`, or several selects sharing an id, leaves the combobox announcing nothing. Only set a
    // name where it is actually missing, so TomSelect's own correct wiring is never overwritten.
    //
    // `focus_node` is TomSelect's own reference to the element it puts `role="combobox"` on, so it
    // is exact. Do not go looking through the DOM for it: the select is a SIBLING of `.ts-wrapper`,
    // not inside it, so a search from the select's parent finds the first combobox in the
    // surrounding card — which on the medic-groups form would name all fifteen widgets after the
    // first one.
    const combobox = this.tomSelect.focus_node
    if (!combobox || combobox.getAttribute('aria-labelledby') || combobox.getAttribute('aria-label')) {
      return
    }

    const fallback = this.labelValue || el.getAttribute('aria-label') || el.getAttribute('title')
    if (fallback) combobox.setAttribute('aria-label', fallback)
  }

  inTableResponsive() {
    return !!this.element.closest('.table-responsive')
  }

  inCardBody() {
    return !!this.element.closest('.card-body')
  }

  inModal() {
    return !!this.element.closest('.modal')
  }

  inFilter() {
    return !!this.element.closest('.filters')
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy()
    }
  }

  async #sortableConditionalImport() {
    if (typeof Sortable === 'undefined') {
        const { default: Sortable } = await import('sortablejs')
        return Sortable
    }
    return window.Sortable
  }
}
